import { Text } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import type { SortingState } from '@tanstack/react-table';
import {
	createFacetedMinMaxValues,
	createFacetedRowModel,
	createFacetedUniqueValues,
	createFilteredRowModel,
	createPaginatedRowModel,
	createSortedRowModel,
	useTable,
} from '@tanstack/react-table';

import { AddIcon } from '@apple/assets/icons';
import { useTranslation } from '@apple/lib/i18next';
import { useCurrentLocation } from '@apple/lib/tanstack-router/hooks/use-router-history';
import { DataTable } from '@apple/ui/data-table/controls/data-table';
import { ToolbarButton } from '@apple/ui/data-table/controls/Toolbar/toolbar-button';
import { createDataTableFeatures } from '@apple/ui/data-table/features';
import { useTableState } from '@apple/ui/data-table/hooks/use-table-state';
import { TableLayout } from '@apple/ui/layouts/TableLayout';
import { Link } from '@apple/ui/link/Link';

import { requireAuth } from '@apple/features/auth/utils/require-auth';
import type { ProgramFilters, ProgramListing } from '@apple/features/program/management.models';
import { programsQueryOptions } from '@apple/features/program/management.queries';

const features = createDataTableFeatures({
	filteredRowModel: createFilteredRowModel(),
	sortedRowModel: createSortedRowModel(),
	paginatedRowModel: createPaginatedRowModel(),
	facetedRowModel: createFacetedRowModel(),
	facetedUniqueValues: createFacetedUniqueValues(),
	facetedMinMaxValues: createFacetedMinMaxValues(),
});

export const Route = createFileRoute('/_authed/_admin/manage/programs')({
	beforeLoad: args => {
		requireAuth(args, {
			requirePermission: 'Manzanita.Security.Features.CustomerGroupManagement.Read',
		});

		return {
			programsQueryOptions: programsQueryOptions,
		};
	},
	loader: ({ context: { queryClient, programsQueryOptions } }) => {
		void queryClient.prefetchQuery(programsQueryOptions);
	},
	wrapInSuspense: true,
	component: ProgramManagementRoute,
});

const defaultFilters: ProgramFilters = {};
const defaultSorting: SortingState = [{ id: 'name', desc: false }];

function ProgramManagementRoute() {
	const { t } = useTranslation('program');
	const navigate = Route.useNavigate();
	const location = useCurrentLocation();
	const search = Route.useSearch();
	const { programsQueryOptions } = Route.useRouteContext();
	const programsQuery = useQuery(programsQueryOptions);

	const tableState = useTableState<ProgramListing, ProgramFilters>({
		search,
		navigate,
		defaultFilters: defaultFilters,
		defaultSorting: defaultSorting,
		fieldMap: [],
	});

	const table = useTable({
		features,
		data: programsQuery.data,
		...tableState,
		enableFilters: true,
		enableColumnFilters: true,
		enableSorting: true,
		columnResizeMode: 'onChange',
		columns: [
			{
				accessorKey: 'name',
				header: t('labels.name'),
				filter: {
					group: t('filterGroups.details'),
				},
				size: 250,
				cell: ({ row }) => (
					<Link
						to='/manage/programs/$programId'
						state={{ from: location }}
						params={{ programId: row.original.id }}
						title={row.original.name}
						children={row.original.name}
					/>
				),
			},
			{
				accessorKey: 'code',
				header: t('labels.customerGroupCode'),
				filter: {
					group: t('filterGroups.details'),
				},
			},
			{
				accessorKey: 'customerCount',
				header: t('labels.customerCount'),
				filter: {
					group: t('filterGroups.details'),
					variant: 'number-range-slider',
				},
				cell: ({ row }) => <Text>{row.original.customerCount}</Text>,
			},
			{
				id: 'status',
				accessorKey: 'status',
				header: t('labels.status'),
				filter: {
					group: t('filterGroups.details'),
					variant: 'select',
					getFilterDisplayValue: value => {
						return value === '1' ? t('labels.active') : t('labels.inactive');
					},
				},
				cell: ({ row }) => {
					const status =
						row.original.status === '1' ? t('labels.active') : t('labels.inactive');
					return <Text>{status}</Text>;
				},
			},
		],
	});

	return (
		<TableLayout
			title={t('title.list')}
			table={table}
			toolbarButtons={[
				<ToolbarButton
					key='add'
					tooltip={t('buttons.add')}
					icon={AddIcon}
					onClick={() =>
						void navigate({
							to: '/manage/programs/add',
							state: { from: location },
						})
					}
					data-testid='add-program-button'
				/>,
			]}
		>
			<DataTable table={table} loading={programsQuery.isFetching} />
		</TableLayout>
	);
}
